import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, gbp2 } from "@/lib/api";

/**
 * CLAÈ Admin — Analytics (admin-only).
 *
 * Automatic ROI / product-cost profitability. Pulls from
 *   GET /api/admin/analytics/overview?date_from=&date_to=&treatment_id=
 *   GET /api/admin/analytics/by-product?date_from=&date_to=
 *
 * Revenue = amount actually charged; cost = estimated product cost
 * snapshotted per completed appointment. All formulas are computed in the
 * API; the page only displays them.
 */
export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [byProduct, setByProduct] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [treatmentFilter, setTreatmentFilter] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(() => {
    try { return localStorage.getItem("clae_show_analytics") !== "0"; } catch { return true; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = new Date(dateFrom + "T00:00:00").toISOString();
      if (dateTo)   params.date_to   = new Date(dateTo   + "T23:59:59").toISOString();
      if (treatmentFilter) params.treatment_id = treatmentFilter;
      const [overview, products] = await Promise.all([
        api.get("/admin/analytics/overview", { params }),
        api.get("/admin/analytics/by-product", { params: { date_from: params.date_from, date_to: params.date_to } }),
      ]);
      setData(overview.data);
      setByProduct(products.data?.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load analytics");
    }
    setLoading(false);
  }, [dateFrom, dateTo, treatmentFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleShow = () => {
    setShowAnalytics((v) => {
      const nv = !v;
      try { localStorage.setItem("clae_show_analytics", nv ? "1" : "0"); } catch {}
      return nv;
    });
  };

  const resetFilters = () => {
    setDateFrom(""); setDateTo(""); setTreatmentFilter("");
  };

  const treatmentOptions = useMemo(() => {
    if (!data?.treatments) return [];
    return [...data.treatments]
      .sort((a, b) => (a.treatment_name || "").localeCompare(b.treatment_name || ""))
      .map((t) => ({ id: t.treatment_id, name: t.treatment_name }));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-stone-900">Analytics</h2>
          <p className="text-xs text-stone-600 mt-1">
            Estimated product-cost profitability. Excludes rent, staff, insurance, utilities, tax, marketing and other business expenses.
          </p>
        </div>
        <button
          data-testid="analytics-toggle-visibility"
          onClick={toggleShow}
          className="mono-label text-xs border border-stone-900 px-3 py-2 hover:bg-stone-900 hover:text-white transition"
        >{showAnalytics ? "Hide" : "Show"} analytics</button>
      </div>

      {showAnalytics && (
        <>
          <div className="border hairline bg-white p-4 flex items-end gap-4 flex-wrap" data-testid="analytics-filters">
            <label className="block">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">FROM</span>
              <input
                data-testid="analytics-date-from"
                type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border hairline px-3 py-2 text-sm focus:outline-none focus:border-stone-900"
              />
            </label>
            <label className="block">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">TO</span>
              <input
                data-testid="analytics-date-to"
                type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border hairline px-3 py-2 text-sm focus:outline-none focus:border-stone-900"
              />
            </label>
            <label className="block">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">TREATMENT</span>
              <select
                data-testid="analytics-treatment-filter"
                value={treatmentFilter}
                onChange={(e) => setTreatmentFilter(e.target.value)}
                className="border hairline px-3 py-2 text-sm focus:outline-none focus:border-stone-900 min-w-0 w-full sm:w-auto sm:min-w-[220px]"
              >
                <option value="">All treatments</option>
                {treatmentOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <button
              data-testid="analytics-reset-btn"
              onClick={resetFilters}
              className="mono-label text-xs border border-stone-400 text-stone-700 px-3 py-2 hover:bg-stone-100 transition"
            >Reset</button>
          </div>

          {loading && <div className="text-sm text-stone-500">Loading…</div>}

          {!loading && data && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="analytics-totals">
                <TotalCard label="Completed Treatments" value={data.totals.completed_count} testId="kpi-completed" />
                <TotalCard label="Actual Revenue" value={gbp2(data.totals.total_revenue)} testId="kpi-revenue" />
                <TotalCard label="Estimated Product Costs" value={gbp2(data.totals.total_product_cost)} testId="kpi-est-cost" />
                <TotalCard label="Estimated Gross Profit" value={gbp2(data.totals.total_gross_profit)} highlight testId="kpi-gross-profit" />
                <TotalCard label="Gross Margin" value={fmtPct(data.totals.overall_margin_pct)} testId="kpi-margin" />
                <TotalCard label="ROI" value={fmtPct(data.totals.overall_roi_pct)} testId="kpi-roi" />
              </div>

              {/* Treatment table */}
              <div className="border hairline bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr className="text-left mono-label text-[11px] text-stone-600">
                      <th className="px-4 py-3">TREATMENT</th>
                      <th className="px-4 py-3 text-right">STANDARD PRICE</th>
                      <th className="px-4 py-3 text-right">EST. PRODUCT COST / TREATMENT</th>
                      <th className="px-4 py-3 text-right border-l hairline">COMPLETED</th>
                      <th className="px-4 py-3 text-right">ACTUAL REVENUE</th>
                      <th className="px-4 py-3 text-right">TOTAL EST. PRODUCT COST</th>
                      <th className="px-4 py-3 text-right">EST. GROSS PROFIT</th>
                      <th className="px-4 py-3 text-right">MARGIN</th>
                      <th className="px-4 py-3 text-right">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.treatments.map((t) => (
                      <tr key={t.treatment_id} data-testid={`analytics-row-${t.treatment_id}`} className="border-t hairline">
                        <td className="px-4 py-3">
                          <span className="font-medium text-stone-900">{t.treatment_name}</span>
                          {t.category && (
                            <span className="block text-[11px] text-stone-500">{t.category}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{gbp2(t.price)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{gbp2(t.estimated_product_cost)}</td>
                        <td className="px-4 py-3 text-right tabular-nums border-l hairline">{t.completed_count}</td>
                        <td className="px-4 py-3 text-right tabular-nums" data-testid={`analytics-revenue-${t.treatment_id}`}>{gbp2(t.total_revenue)}</td>
                        <td className="px-4 py-3 text-right tabular-nums" data-testid={`analytics-est-cost-${t.treatment_id}`}>{gbp2(t.total_product_cost)}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium" data-testid={`analytics-profit-${t.treatment_id}`}>{gbp2(t.total_gross_profit)}</td>
                        <td className="px-4 py-3 text-right tabular-nums" data-testid={`analytics-margin-${t.treatment_id}`}>{fmtPct(t.margin_pct)}</td>
                        <td className="px-4 py-3 text-right tabular-nums" data-testid={`analytics-roi-${t.treatment_id}`}>{fmtPct(t.roi_pct)}</td>
                      </tr>
                    ))}
                    {data.treatments.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-6 text-center text-stone-500">
                          No treatments to show for the current filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* By-product */}
              <div>
                <h3 className="font-serif text-lg text-stone-900 mb-2">By product (period actuals)</h3>
                <div className="border hairline bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50">
                      <tr className="text-left mono-label text-[11px] text-stone-600">
                        <th className="px-4 py-3">PRODUCT</th>
                        <th className="px-4 py-3 text-right">UNITS USED</th>
                        <th className="px-4 py-3 text-right">COMPLETIONS</th>
                        <th className="px-4 py-3 text-right">TOTAL COST (frozen)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byProduct.map((p) => (
                        <tr key={p.stock_item_id || p.stock_item_name} className="border-t hairline" data-testid={`analytics-product-${p.stock_item_id}`}>
                          <td className="px-4 py-3">{p.stock_item_name || <span className="text-stone-400">— deleted —</span>}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{p.units_used}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{p.deductions}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{gbp2(p.total_cost)}</td>
                        </tr>
                      ))}
                      {byProduct.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-4 py-6 text-center text-stone-500">
                            No product consumption recorded in the current period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-[11px] text-stone-500">{data.disclaimer}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}

function TotalCard({ label, value, highlight, testId }) {
  return (
    <div data-testid={testId} className={`border hairline p-4 ${highlight ? "bg-stone-900 text-white" : "bg-white"}`}>
      <p className={`mono-label text-[10px] ${highlight ? "text-stone-300" : "text-stone-500"}`}>{label}</p>
      <p className={`font-serif text-xl mt-1 tabular-nums ${highlight ? "text-white" : "text-stone-900"}`}>{value}</p>
    </div>
  );
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2)}%`;
}
